<!--
  @component CodeWorkspace — in-browser file tree + Monaco editor (Tier 2 v2).
  tier:      consumer panel (Code screen)
  reads:     useDeploymentIO → workspace.files.* (list/read/write/delete)
                             + workspace.git.commit (commit + push to Gitea)
  engine:    @liquidefy/code-editor via createEditorEngine(dispatch, projectId)
             — the engine's fs is bound to this project's workspace; the diff
             namespace powers the Review panel. The editor view is monaco.
  purpose:   edit your project's files in the browser → review the diff →
             commit → the build vertical builds + deploys. Workspace ops are
             path-scoped + owner-gated server-side.
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../global/io/nodes/deployment/DeploymentIO.svelte";
  import { createEditorEngine, type EditorEngine } from "../../../global/io/nodes/code-editor/code-editor-io.js";
  import MonacoEditor, { languageForPath } from "./MonacoEditor.svelte";

  interface Props { projectId: string; }
  const { projectId }: Props = $props();

  const node = useDeploymentIO();
  // The code-editor engine, with its filesystem bound to this project's
  // workspace over the dispatch seam. Re-derived if the project changes
  // (svelte-spa-router reuses this component across /projects/:id swaps).
  const engine: EditorEngine = $derived.by(() =>
    createEditorEngine((op, payload) => node.dispatch(op, payload), projectId),
  );

  interface FileMeta { path: string; size: number; updatedAtMs: number; }
  let files = $state<readonly FileMeta[]>([]);
  let openPath = $state<string | null>(null);
  let content = $state<string>("");
  let original = $state<string>("");
  let loading = $state(true);
  let saving = $state(false);
  let error = $state<string | null>(null);
  let newPath = $state("");
  let reviewing = $state(false);

  // Commit / build (T2D).
  let committing = $state(false);
  let commitMsg = $state("");
  let commitNote = $state<string | null>(null);

  const dirty = $derived(content !== original);
  const language = $derived(openPath ? languageForPath(openPath) : "plaintext");
  const lineCount = $derived(content ? content.split("\n").length : 0);

  // The unified diff (working tree vs last-saved), via the engine's diff
  // domain. Empty unless we're reviewing a dirty file.
  const diffText = $derived.by(() => {
    if (!reviewing || !dirty) return "";
    const r = engine.diff.compute(original, content);
    return r.success ? engine.diff.formatUnified(r.value, 3) : "";
  });
  const diffLines = $derived(diffText ? diffText.split("\n") : []);
  const diffStat = $derived.by(() => {
    let added = 0, removed = 0;
    for (const l of diffLines) {
      if (l.startsWith("+") && !l.startsWith("+++")) added++;
      else if (l.startsWith("-") && !l.startsWith("---")) removed++;
    }
    return { added, removed };
  });

  async function loadFiles(): Promise<void> {
    if (!projectId) return;
    loading = true; error = null;
    try {
      const r = (await node.dispatch("workspace.files.list", { projectId })) as { files?: FileMeta[] };
      files = r?.files ?? [];
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { loading = false; }
  }
  $effect(() => { void loadFiles(); });

  async function open(path: string): Promise<void> {
    error = null; reviewing = false;
    try {
      const r = (await node.dispatch("workspace.files.read", { projectId, path })) as { content?: string | null };
      openPath = path; content = r?.content ?? ""; original = content;
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
  }

  async function save(): Promise<void> {
    if (!openPath || saving) return;
    saving = true; error = null;
    try {
      await node.dispatch("workspace.files.write", { projectId, path: openPath, content });
      original = content;
      reviewing = false;
      await loadFiles();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { saving = false; }
  }

  async function createFile(): Promise<void> {
    const path = newPath.trim();
    if (!path) return;
    error = null;
    try {
      await node.dispatch("workspace.files.write", { projectId, path, content: "" });
      newPath = "";
      await loadFiles();
      await open(path);
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
  }

  async function remove(path: string): Promise<void> {
    error = null;
    try {
      await node.dispatch("workspace.files.delete", { projectId, path });
      if (openPath === path) { openPath = null; content = ""; original = ""; reviewing = false; }
      await loadFiles();
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
  }

  async function commit(): Promise<void> {
    committing = true; commitNote = null; error = null;
    try {
      const r = (await node.dispatch("workspace.git.commit", { projectId, message: commitMsg.trim() || "Update from Liquidefy editor" })) as { ok?: boolean; note?: string; error?: string };
      commitNote = r?.error ?? r?.note ?? "committed";
      commitMsg = "";
    } catch (e) { error = e instanceof Error ? e.message : String(e); }
    finally { committing = false; }
  }
</script>

<div class="code">
  {#if error}<p class="err">{error}</p>{/if}
  <div class="panes">
    <!-- File tree -->
    <aside class="tree">
      <div class="new">
        <input type="text" placeholder="new/file.ts" bind:value={newPath} spellcheck="false" />
        <button type="button" disabled={!newPath.trim()} onclick={() => void createFile()}>+</button>
      </div>
      {#if loading}
        <p class="muted">Loading…</p>
      {:else if files.length === 0}
        <p class="muted">No files yet. Create one above, or connect a repo on the Deployments screen.</p>
      {:else}
        <ul>
          {#each files as f (f.path)}
            <li class:active={f.path === openPath}>
              <button type="button" class="file" onclick={() => void open(f.path)}>{f.path}</button>
              <button type="button" class="del" title="Delete" onclick={() => void remove(f.path)}>✕</button>
            </li>
          {/each}
        </ul>
      {/if}
    </aside>

    <!-- Editor -->
    <section class="editor">
      {#if openPath}
        <header class="ed-head">
          <code class="ed-path">{openPath}{dirty ? " •" : ""}</code>
          <div class="ed-actions">
            <button type="button" class="ghost" disabled={!dirty} class:on={reviewing} onclick={() => (reviewing = !reviewing)}>
              {reviewing ? "Edit" : "Review"}
            </button>
            <button type="button" class="save" disabled={!dirty || saving} onclick={() => void save()}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </header>

        {#if reviewing}
          <div class="diff" role="document">
            {#if diffLines.length === 0}
              <p class="diff-empty">No changes to review.</p>
            {:else}
              <div class="diff-body">{#each diffLines as l, i (i)}<span
                class="dl"
                class:add={l.startsWith("+") && !l.startsWith("+++")}
                class:del={l.startsWith("-") && !l.startsWith("---")}
                class:hunk={l.startsWith("@@")}
                class:meta={l.startsWith("+++") || l.startsWith("---")}
              >{l || " "}</span>{/each}</div>
            {/if}
          </div>
        {:else}
          <div class="ed-view">
            <MonacoEditor bind:value={content} {language} />
          </div>
        {/if}

        <footer class="status">
          <span>{language}</span>
          <span>{lineCount} {lineCount === 1 ? "line" : "lines"}</span>
          {#if reviewing && (diffStat.added || diffStat.removed)}
            <span class="stat add">+{diffStat.added}</span>
            <span class="stat del">−{diffStat.removed}</span>
          {/if}
        </footer>
      {:else}
        <div class="empty"><p>Select a file to edit, or create one.</p></div>
      {/if}
    </section>
  </div>

  <!-- Commit & build -->
  <div class="commit">
    <input type="text" placeholder="commit message" bind:value={commitMsg} />
    <button type="button" class="commit-btn" disabled={committing} onclick={() => void commit()}>{committing ? "Committing…" : "Commit & push"}</button>
    {#if commitNote}<span class="commit-note">{commitNote}</span>{/if}
  </div>
  <p class="hint">Commit pushes the workspace to your repo; a build is triggered from the Deployments screen.</p>
</div>

<style>
  .code { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--type-font); }
  .err { margin: 0; font-size: 12px; color: var(--error); }
  .muted { margin: 0; font-size: 12.5px; color: var(--text-tertiary); line-height: 1.5; padding: var(--space-2); }
  .panes { display: grid; grid-template-columns: 240px 1fr; gap: var(--space-3); height: 520px; border: 0.5px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
  .tree { border-right: 0.5px solid var(--border); overflow-y: auto; background: var(--surface-sunken); display: flex; flex-direction: column; }
  .new { display: flex; gap: var(--space-1); padding: var(--space-2); border-bottom: 0.5px solid var(--border-subtle); }
  .new input { flex: 1; min-width: 0; padding: 5px var(--space-2); background: var(--surface-base); border: 0.5px solid var(--border); border-radius: var(--radius-sm); color: var(--text-primary); font: inherit; font-size: 11.5px; font-family: var(--type-mono); }
  .new button { appearance: none; background: var(--bg-active); border: 0.5px solid var(--border-strong); border-radius: var(--radius-sm); color: var(--text-primary); cursor: pointer; width: 28px; font-size: 14px; }
  .new button:disabled { opacity: 0.4; cursor: default; }
  .tree ul { list-style: none; margin: 0; padding: var(--space-1); display: flex; flex-direction: column; }
  .tree li { display: flex; align-items: center; border-radius: var(--radius-sm); }
  .tree li.active { background: var(--bg-active); }
  .file { flex: 1; min-width: 0; appearance: none; background: transparent; border: 0; text-align: left; color: var(--text-secondary); font: inherit; font-size: 12px; font-family: var(--type-mono); padding: 5px var(--space-2); cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .tree li.active .file { color: var(--text-primary); }
  .file:hover { color: var(--text-primary); }
  .del { appearance: none; background: transparent; border: 0; color: var(--text-ghost); font-size: 10px; cursor: pointer; padding: 0 6px; }
  .del:hover { color: var(--error); }

  .editor { display: flex; flex-direction: column; min-width: 0; }
  .ed-head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); padding: var(--space-2) var(--space-3); border-bottom: 0.5px solid var(--border-subtle); }
  .ed-path { font-family: var(--type-mono); font-size: 12px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .ed-actions { display: flex; gap: var(--space-2); flex-shrink: 0; }
  .save, .ghost { appearance: none; border-radius: var(--radius-sm); font: inherit; font-size: 12px; padding: 4px var(--space-3); cursor: pointer; }
  .save { background: var(--bg-active); border: 0.5px solid var(--border-strong); color: var(--text-primary); }
  .save:disabled { opacity: 0.45; cursor: default; }
  .ghost { background: transparent; border: 0.5px solid var(--border); color: var(--text-secondary); }
  .ghost:hover:not(:disabled) { color: var(--text-primary); border-color: var(--border-strong); }
  .ghost.on { color: var(--text-primary); border-color: var(--border-strong); background: var(--bg-active); }
  .ghost:disabled { opacity: 0.4; cursor: default; }
  .ed-view { flex: 1; min-height: 0; background: var(--surface-base); }

  .diff { flex: 1; min-height: 0; overflow: auto; background: var(--surface-base); }
  .diff-empty { margin: 0; padding: var(--space-3); font-size: 12.5px; color: var(--text-tertiary); }
  .diff-body { margin: 0; padding: var(--space-2) 0; font-family: var(--type-mono); font-size: 12px; line-height: 1.55; tab-size: 2; }
  .dl { display: block; padding: 0 var(--space-3); white-space: pre; color: var(--text-secondary); border-left: 2px solid transparent; }
  .dl.add { color: var(--success); border-left-color: var(--success); }
  .dl.del { color: var(--error); border-left-color: var(--error); }
  .dl.hunk { color: var(--accent); }
  .dl.meta { color: var(--text-tertiary); }

  .status { display: flex; align-items: center; gap: var(--space-3); padding: 5px var(--space-3); border-top: 0.5px solid var(--border-subtle); font-size: 11px; font-family: var(--type-mono); color: var(--text-tertiary); }
  .status .stat.add { color: var(--success); }
  .status .stat.del { color: var(--error); }
  .empty { flex: 1; display: grid; place-items: center; color: var(--text-tertiary); font-size: 13px; }

  .commit { display: flex; gap: var(--space-2); align-items: center; }
  .commit input { flex: 1; min-width: 0; padding: 7px var(--space-3); background: var(--surface-sunken); border: 0.5px solid var(--border); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 12.5px; }
  .commit-btn { appearance: none; background: var(--bg-active); border: 0.5px solid var(--border-strong); border-radius: var(--radius-md); color: var(--text-primary); font: inherit; font-size: 12.5px; font-weight: 500; padding: 7px var(--space-4); cursor: pointer; white-space: nowrap; }
  .commit-btn:disabled { opacity: 0.45; cursor: default; }
  .commit-note { font-size: 11.5px; color: var(--text-tertiary); }
  .hint { margin: 0; font-size: 11.5px; color: var(--text-tertiary); }
</style>
